import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth.js";
import { getPaymentsDue, paymentDueStudentCount, paymentsDueRange, type PaymentDueLesson } from "../booking/paymentsDue.js";
import { IncomeDetailPanel, IncomeStatCard } from "../earnings/IncomeDisclosure.js";
import { formatDate, formatDateTime } from "../format.js";
import { Pagination, usePagination } from "../Pagination.js";
import { DENSE_ROW_PAGE_SIZE } from "../paginationPageSizes.js";
import { useAsync } from "../useAsync.js";
import { STUDENT_CREDIT_STATE_CHANGED_EVENT } from "../booking/studentCreditEvents.js";
import { useNow } from "../useNow.js";
import { studentDetailHref } from "./Students.js";
import "./paymentsDue.css";

export function usePaymentsDue(days: 1 | 7, revision = 0) {
  const { me } = useAuth();
  const allowed = me?.booking.staff === true && me.linkedToProvider !== false &&
    me.capabilities.includes("reports.payments.read");
  const now = useNow(60_000);
  const range = paymentsDueRange(days, new Date(now));
  const result = useAsync(() => allowed ? getPaymentsDue(range.start, range.end) : Promise.resolve(null),
    [allowed, range.start, range.end, me?.user.id, revision], { reloadOnMirrorRefresh: true });
  useEffect(() => {
    if (!allowed) return;
    const refresh = () => result.reload();
    window.addEventListener(STUDENT_CREDIT_STATE_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(STUDENT_CREDIT_STATE_CHANGED_EVENT, refresh);
  }, [allowed, result.reload]);
  const report = !result.loading && !result.error ? result.data : null;
  const applicable = allowed && (result.loading || result.error !== null || result.data !== null);
  return { ...result, report, allowed: applicable, range,
    count: report ? paymentDueStudentCount(report.items) : null,
    reviewCount: report?.items.filter((item) => item.state === "needs_check").length ?? 0,
  };
}

type DueState = ReturnType<typeof usePaymentsDue>;
function lessonsNeedingCheck(count: number): string {
  return `${count} ${count === 1 ? "lesson needs" : "lessons need"} checking`;
}

export function PaymentsDueCard({ state, open, onToggle }: { state: DueState; open: boolean; onToggle: () => void }) {
  if (!state.allowed) return null;
  return <IncomeStatCard id="upcoming-payments-due" title="Due in the next 7 days" action="Payments due in the next 7 days" linkLabel="View students" value={state.loading ? "…" : state.count ?? "—"} open={open} onToggle={onToggle}>
    <span>{state.error ? "Payment checks unavailable" : "Students with no payment or usable credit"}</span>
    <span>Today and the following 6 days · your private lessons</span>
    {state.reviewCount > 0 && <span className="income-stat-warning">{lessonsNeedingCheck(state.reviewCount)}</span>}
  </IncomeStatCard>;
}

export function PaymentsDueDetails({ state, open, onClose }: { state: DueState; open: boolean; onClose: () => void }) {
  if (!state.allowed) return null;
  return <IncomeDetailPanel id="upcoming-payments-due" title="Due in the next 7 days" open={open} onClose={onClose}>
    <PaymentsDueList state={state} />
  </IncomeDetailPanel>;
}

/** Today's disclosure shares the same authoritative projection as Income. */
export function TodayPaymentsDue({ revision = 0 }: { revision?: number }) {
  const state = usePaymentsDue(1, revision);
  const [open, setOpen] = useState(false);
  if (!state.allowed) return null;
  function close() {
    setOpen(false);
    document.getElementById("today-payments-due-toggle")?.focus();
  }
  return <section className="dashboard-panel payments-due-today">
    <button id="today-payments-due-toggle" type="button" className="payments-due-toggle" aria-expanded={open} aria-controls="today-payments-due-list" onClick={() => setOpen(!open)}>
      <span><strong>Payments Due</strong><span className="small muted">Your private lessons today</span></span>
      <span className="payments-due-count">{state.loading ? "…" : state.count ?? "—"}<span className="small muted"> {state.count === 1 ? "student" : "students"}</span></span>
      <span aria-hidden="true">{open ? "−" : "+"}</span>
    </button>
    {!open && <p className="small muted payments-due-summary">{state.error ? "Payment checks unavailable — open to retry." : state.reviewCount ? `${lessonsNeedingCheck(state.reviewCount)} before requesting payment.` : "Checks confirmed payments and usable account credits."}</p>}
    <div id="today-payments-due-list" hidden={!open} onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); close(); } }}>
      <PaymentsDueList state={state} />
      <button type="button" className="quiet" onClick={close}>Close Payments Due</button>
    </div>
  </section>;
}

function PaymentsDueList({ state }: { state: DueState }) {
  const [filter, setFilter] = useState<PaymentDueLesson["state"]>("due");
  const rows = useMemo(() => state.report?.items.filter((item) => item.state === filter) ?? [], [state.report, filter]);
  const pagination = usePagination(
    rows,
    DENSE_ROW_PAGE_SIZE,
    `${state.range.start}:${state.range.end}:${filter}`,
  );
  return <div className="payments-due-content">
    <p className="small muted">{formatDate(`${state.range.start}T12:00:00Z`)} to {formatDate(`${state.range.end}T12:00:00Z`)}. Credits must match the student, tutor, lesson type and expiry. Available credits cover one lesson at a time, earliest first; this preview does not apply or reserve them.</p>
    {state.loading ? <p role="status">Checking payments and available credits…</p> : state.error ? <div className="notice" role="status">
      <p>Payments Due could not be checked. A blank list does not mean everyone has paid.</p>
      <button type="button" className="secondary" onClick={state.reload}>Retry payment checks</button>
    </div> : state.report && <>
      <div className="payments-due-toolbar">
        <label>Show <select value={filter} onChange={(event) => setFilter(event.target.value as PaymentDueLesson["state"])}>
          <option value="due">Payment due ({state.report.items.filter((item) => item.state === "due").length})</option>
          <option value="credit_available">Credit available, not applied ({state.report.items.filter((item) => item.state === "credit_available").length})</option>
          <option value="needs_check">Needs checking ({state.reviewCount})</option>
        </select></label>
        <button type="button" className="quiet" onClick={state.reload}>Refresh payments and credits</button>
      </div>
      {state.reviewCount > 0 && filter !== "needs_check" && <p className="notice">{lessonsNeedingCheck(state.reviewCount)} before requesting payment. Select “Needs checking” to see the details.</p>}
      {rows.length === 0 ? <p className="empty">{filter === "due" ? "No confirmed payments due for these lessons." : filter === "credit_available" ? "No lessons are covered by unapplied credit." : "No lessons need checking."}</p> : <ul role="list" className="payments-due-students">
        {pagination.items.map((item) => {
          const profileHref = item.portal_student_id ? studentDetailHref(item.portal_student_id, "mine") : null;
          return <li key={`${item.appointment_id}:${item.booking_id}`}>
            <div className="payments-due-learner">
              {profileHref ? <Link to={profileHref}>{item.student_name}</Link> : <strong>{item.student_name}</strong>}
              <span className="small muted">{formatDateTime(item.starts_at)} · {item.service_name}</span>
              <span className="small muted">{item.reason}</span>
            </div>
            <div className="payments-due-actions">
              {item.state === "due" && item.amount_due !== null && <strong>£{item.amount_due}</strong>}
              {profileHref
                ? <Link to={`${profileHref}&tab=lessons`}>View lessons</Link>
                : <Link to="/diary">Open Diary</Link>}
            </div>
          </li>;
        })}
      </ul>}
      <Pagination page={pagination.page} pages={pagination.pages} onPrevious={pagination.previous} onNext={pagination.next} ariaLabel="Payments due pagination" />
      <p className="small muted">{state.report.settled_lessons} {state.report.settled_lessons === 1 ? "lesson" : "lessons"} already paid or funded. Checked {formatDateTime(state.report.evaluated_at)}. School billing is separate.</p>
    </>}
  </div>;
}
